/**
 * Header — Paean Family branded session header.
 *
 * Layout adapts gracefully:
 *  • Wide terminals (≥ NARROW_TERMINAL_THRESHOLD): three-row arch logo +
 *    name / status / cwd column + family sigil aligned to the right.
 *  • Narrow terminals: collapses to a single compact line so we never
 *    waste the user's vertical real estate.
 */

import React, { useMemo } from 'react';
import { Box, Text } from 'ink';
import {
    ASCII_LOGO,
    COMPACT_LOGO,
    SEPARATOR_CHAR,
    COLORS,
    FAMILY_SIGIL,
    NARROW_TERMINAL_THRESHOLD,
    SYMBOLS,
} from '../theme/ink-theme.js';
import type { LogoSegment } from '../theme/ink-theme.js';
import { useTerminalSize } from '../hooks/index.js';

interface HeaderProps {
    mcpToolCount?: number;
    gatewayOnline?: boolean;
    gatewayCompleted?: number;
    wechatOnline?: boolean;
    wechatCompleted?: number;
}

const LogoLine: React.FC<{ segments: LogoSegment[] }> = ({ segments }) => (
    <>
        {segments.map((seg, i) => (
            <Text key={i} color={seg.accent ? COLORS.primary : COLORS.primaryDim}>
                {seg.text}
            </Text>
        ))}
    </>
);

/**
 * Replace user's home dir with `~` for compact display.
 * Defensive: if HOME is empty/missing, still returns a usable string.
 */
function getDisplayCwd(): string {
    const home = process.env['HOME'];
    const cwd = process.cwd();
    if (home && home.length > 0 && cwd.startsWith(home)) {
        return '~' + cwd.slice(home.length);
    }
    return cwd;
}

/**
 * Truncate a path to fit the available width by collapsing leading
 * segments to `…/` while always preserving the last two path segments
 * so the user still recognizes where they are.
 */
function truncatePath(path: string, maxWidth: number): string {
    if (path.length <= maxWidth) return path;
    const segs = path.split('/');
    if (segs.length <= 2) {
        // Hard truncate from the left
        return '…' + path.slice(-(maxWidth - 1));
    }
    const tail = segs.slice(-2).join('/');
    const candidate = '…/' + tail;
    if (candidate.length <= maxWidth) return candidate;
    return '…' + tail.slice(-(maxWidth - 1));
}

export const Header: React.FC<HeaderProps> = ({
    mcpToolCount,
    gatewayOnline,
    wechatOnline,
}) => {
    const { columns } = useTerminalSize();
    const safeColumns = Math.max(20, columns || 80);
    const isNarrow = safeColumns < NARROW_TERMINAL_THRESHOLD;

    const separatorLine = useMemo(
        () => SEPARATOR_CHAR.repeat(Math.max(0, safeColumns - 2)),
        [safeColumns],
    );

    const cwd = useMemo(() => {
        const raw = getDisplayCwd();
        // Reserve room for ~12 chars of decoration on the cwd line
        const budget = Math.max(20, safeColumns - 12);
        return truncatePath(raw, budget);
    }, [safeColumns]);

    const badges = useMemo(() => {
        const b: Array<{ label: string; color: string }> = [];
        if (mcpToolCount && mcpToolCount > 0) {
            b.push({
                label: `${SYMBOLS.tool} ${mcpToolCount} ${mcpToolCount === 1 ? 'tool' : 'tools'}`,
                color: COLORS.success,
            });
        }
        if (gatewayOnline) {
            b.push({ label: `${SYMBOLS.remote} Gateway`, color: COLORS.success });
        }
        if (wechatOnline) {
            b.push({ label: `${SYMBOLS.wechat} WeChat`, color: '#07C160' });
        }
        return b;
    }, [mcpToolCount, gatewayOnline, wechatOnline]);

    // Compact rendering — narrow terminals get a single status line.
    if (isNarrow) {
        return (
            <Box flexDirection="column" marginBottom={0}>
                <Box>
                    <Text color={COLORS.primary} bold>{COMPACT_LOGO} </Text>
                    {badges.length > 0 ? (
                        <>
                            {badges.map((b, i) => (
                                <Text key={i} color={b.color}>
                                    {i > 0 ? '  ' : ''}{b.label}
                                </Text>
                            ))}
                        </>
                    ) : (
                        <Text dimColor>Interactive Session</Text>
                    )}
                </Box>
                <Box>
                    <Text dimColor>{cwd}</Text>
                </Box>
                <Box>
                    <Text color={COLORS.muted}>{separatorLine}</Text>
                </Box>
            </Box>
        );
    }

    // Wide rendering — three-row arch lockup + info column + family sigil.
    const infoElements = [
        <Box key="name" gap={1}>
            <Text color={COLORS.primary} bold>Paean Agent CLI</Text>
            <Text color={COLORS.muted}>{FAMILY_SIGIL}</Text>
        </Box>,
        badges.length > 0 ? (
            <Box key="badges" gap={2}>
                {badges.map((b, i) => (
                    <Text key={i} color={b.color}>{b.label}</Text>
                ))}
            </Box>
        ) : (
            <Text key="status" dimColor>Interactive Session — type {SYMBOLS.chevronRight} <Text color={COLORS.accent}>/help</Text> to begin</Text>
        ),
        <Text key="cwd" dimColor>{cwd}</Text>,
    ];

    return (
        <Box flexDirection="column" marginBottom={0}>
            {ASCII_LOGO.map((segments, i) => (
                <Box key={i}>
                    <LogoLine segments={segments} />
                    {infoElements[i] && (
                        <>
                            <Text>  </Text>
                            {infoElements[i]}
                        </>
                    )}
                </Box>
            ))}
            <Box>
                <Text color={COLORS.muted}>{separatorLine}</Text>
            </Box>
        </Box>
    );
};
